import React from "react";
import {
  columnSortingFunctions,
  TableCells,
  ProgressBar,
} from "@pushpress/shared-components";
import {
  addMonths,
  formatDuration,
  isBefore,
  startOfMonth,
  intervalToDuration,
} from "date-fns";
import { TableColumn } from "@pushpress/shared-components/build/components/tables/Table/types";
import styles from "./ChurnReport.module.scss";

// This is the date when the feature was released, we use this as the filter limit too
const releaseDate = new Date("2022-10-31");

export const HIGH_RISK_TEXT = "HIGH";
export const MEDIUM_RISK_TEXT = "MEDIUM";
export const HIGH_RISK_COLOR = "#FFA39E";
export const MEDIUM_RISK_COLOR = "#FFE58F";

export function getRiskInfo(
  churnProbability: ["churnProbability"],
) {
  const riskLabel = isHighRisk(Number(churnProbability))
    ? HIGH_RISK_TEXT
    : MEDIUM_RISK_TEXT;
  const riskColor = isHighRisk(Number(churnProbability))
    ? HIGH_RISK_COLOR
    : MEDIUM_RISK_COLOR;

  return {
    riskLabel,
    riskColor,
  };
}

export const getMonthsBetweenTwoDates = (
  today = new Date(),
  limit = releaseDate,
): Date[] => {
  const result = [];

  let currentDate = limit;

  while (isBefore(currentDate, startOfMonth(today))) {
    result.push(currentDate);
    currentDate = addMonths(currentDate, 1);
  }

  result.push(today);

  return result;
};

export const filterDatesList: Date[] = getMonthsBetweenTwoDates(
  new Date(),
  releaseDate,
).reverse();

export const isHighRisk = (risk: number): boolean => risk > 0.65;

export const getMemberLength = (from: Date, to: Date): string => {
  const interval = intervalToDuration({
    start: from,
    end: to,
  });

  return formatDuration(interval, { format: ["years", "months", "days"] });
};

export const getMemberLastVisit = (lastVisit: number): string => {
  if (lastVisit === 0) return "N/A";

  const interval = intervalToDuration({
    start: lastVisit,
    end: new Date(),
  });

  return formatDuration(interval, { format: ["years", "months", "days"] });
};

const nameColumn: TableColumn = {
  name: "Name",
  dataKey: "name",
  skeletonWidthPct: 60,
  sortFunc: columnSortingFunctions.string,
  renderCell: (fullName: string) => {
    const idx = fullName.lastIndexOf(" ");
    const firstName = fullName.slice(0, idx);
    const lastName = fullName.slice(idx + 1);
    return (
      <div className={styles.nameCell}>
        <TableCells.NameMonogramCell
          firstName={firstName.padEnd(25, " ")}
          lastName={lastName}
        />
      </div>
    );
  },
};
const lastVisitColumn: TableColumn = {
  dataKey: "lastVisit",
  name: "Last Visit",
  renderCell: (lastVisit) => (
    <TableCells.LabelCell text={getMemberLastVisit(lastVisit)} />
  ),
  skeletonWidthPct: 35,
  sortFunc: columnSortingFunctions.string,
};

const churnProbabilityColumn: TableColumn = {
  dataKey: "churnProbability",
  name: "Churn probability",
  renderCell: (data) => {
    const { riskLabel, riskColor } = getRiskInfo(data);

    return (
      <TableCells.TableCell>
        <div className={styles.churnCell}>
          <ProgressBar
            label={riskLabel}
            color={riskColor}
            progressWidth={Math.round(data * 100)}
          />
        </div>
      </TableCells.TableCell>
    );
  },
  skeletonWidthPct: 22,
  sortFunc: columnSortingFunctions.number,
};

export const desktopColumns: TableColumn[] = [
  nameColumn,
  lastVisitColumn,
  churnProbabilityColumn,
];
export const mobileColumns: TableColumn[] = [
  nameColumn,
  churnProbabilityColumn,
];
